import {
  ApplicationInsights,
  DistributedTracingModes,
} from "@microsoft/applicationinsights-web";
import { generateW3CId } from "@microsoft/applicationinsights-core-js";
import { useCommonStore } from "@/stores/CommonStore";
import { useLoginStore } from "@/stores/LoginStore";

// Add page visibility tracking
let isPageVisible = !document.hidden;
let lastFlushTime = 0;
const FLUSH_THROTTLE_MS = 10000; // Only flush every 10 seconds

// Deferred initialization state
let appInsightsInitialized = false;

/**
 * Install function passed to Vue.use() show documentation on vue.js website.
 *
 * @param Vue
 * @param options
 */
function install(Vue, options) {
  // Create a no-op implementation initially
  Vue.appInsights = createNoOpAppInsights();

  // Add a method to initialize App Insights when user data is ready
  Vue.appInsights.initializeWhenReady = () => {
    if (!appInsightsInitialized && shouldEnableAppInsights()) {
      initializeAppInsights(Vue, options);
      appInsightsInitialized = true;
    }
  };

  // Make AppInsights available via provide/inject for Vue 3
  Vue.provide("$appInsights", Vue.appInsights);
  // Also set on global properties for backwards compatibility
  Vue.config.globalProperties.$appInsights = Vue.appInsights;

  const router = options.router;

  // Watch route event if router option is defined.
  if (router) {
    if (options.trackInitialPageView !== false) {
      setupPageTracking(options, Vue);
    } else {
      router.onReady(() => setupPageTracking(options, Vue));
    }
  }
}

/**
 * Check if Application Insights should be enabled for the current user
 */
function shouldEnableAppInsights() {
  // Always enable in non-production environments for debugging
  if (import.meta.env.VITE_NODE_ENV !== "production") {
    return true;
  }

  try {
    const commonStore = useCommonStore();
    return commonStore.fullUserInfo?.traceUser === true;
  } catch (error) {
    // If stores aren't available yet, return false
    return false;
  }
}

/**
 * Initialize the actual Application Insights instance
 */
function initializeAppInsights(Vue, options) {
  const config = options.appInsightsConfig || {};
  config.connectionString = config.connectionString || options.connectionString;

  // Add rate limiting and sampling configuration
  config.samplingPercentage = 30; // Reduce sampling to prevent rate limits
  config.maxBatchInterval = 15000; // Send batches every 15 seconds
  config.maxBatchSizeInBytes = 102400; // Reduce batch size
  config.isRetryDisabled = false;
  config.isStorageUseDisabled = false;

  // Required for end-to-end traces
  config.disableAjaxTracking = false;
  config.disableCorrelationHeaders = false;
  config.disableFetchTracking = false;
  config.enableCorsCorrelation = true;
  config.enableRequestHeaderTracking = true;
  config.enableDebug = import.meta.env.VITE_NODE_ENV !== "production";
  config.loggingLevelConsole =
    import.meta.env.VITE_NODE_ENV !== "production" ? 2 : 0;
  config.distributedTracingMode = DistributedTracingModes.W3C;
  config.loggingLevelTelemetry =
    import.meta.env.VITE_NODE_ENV !== "production" ? 2 : 0;

  const url = import.meta.env.VITE_APP_API_BASE_URL.replace(
    /^\/\/|^.*?:(\/\/)?/,
    "",
  ).replace("/", "");
  config.correlationHeaderDomains = [url, url + ":443"];

  if (options.appInsights) {
    Vue.appInsights = options.appInsights;
  } else {
    const realAppInsights = new ApplicationInsights({ config });

    // Add error filtering for AI internal errors
    realAppInsights.addTelemetryInitializer((envelope) => {
      // Filter out Application Insights internal errors
      if (envelope.baseType === "ExceptionData") {
        const exception = envelope.baseData;
        if (exception && exception.exceptions) {
          for (let ex of exception.exceptions) {
            // Skip AI internal errors
            if (
              ex.message &&
              (ex.message.includes("AI (Internal)") ||
                ex.message.includes("Failed to monitor XMLHttpRequest") ||
                ex.message.includes("Failed to calculate the duration") ||
                ex.message.includes("readystatechange") ||
                ex.message.includes("monitoring data for this ajax call"))
            ) {
              return false; // Don't send this telemetry
            }
          }
        }
      }

      const telemetryItem = envelope.baseData;

      // List of endpoints to ignore
      const ignoredEndpoints = [
        "/service-worker.js", // call to check for new service worker (happens every 5 minutes)
        "cdp.talkingparents.com", // calls to bloomreach
        "firebaseremoteconfig.googleapis.com", // calls to Firebase
      ];

      // Check if the telemetry item is an AJAX call
      if (telemetryItem && telemetryItem.target) {
        // Ignore the request if the URL matches any of the ignored endpoints
        if (
          ignoredEndpoints.some((endpoint) =>
            telemetryItem.target.includes(endpoint),
          )
        ) {
          return false; // Returning false will filter out this telemetry item
        }
      }

      // Reduce tracking for background tabs
      if (!isPageVisible && envelope.baseType === "RemoteDependencyData") {
        // Only track critical requests when page is not visible
        return false;
      }

      // Proceed with sending telemetry
      return true;
    });

    // Replace the no-op instance with the real one
    Vue.appInsights = realAppInsights;
    Vue.appInsights.initializeWhenReady = () => {}; // No-op since it's already initialized

    realAppInsights.loadAppInsights();
    realAppInsights.trackPageView();

    // Setup page visibility handling
    setupPageVisibilityHandling(realAppInsights);

    if (typeof options.onAfterScriptLoaded === "function") {
      options.onAfterScriptLoaded(realAppInsights);
    }

    console.log("Application Insights initialized for traced user");
  }
}

/**
 * Create a no-op implementation of App Insights
 */
function createNoOpAppInsights() {
  return {
    trackPageView: () => {},
    trackEvent: () => {},
    trackException: () => {},
    trackTrace: () => {},
    trackMetric: () => {},
    startTrackPage: () => {},
    stopTrackPage: () => {},
    flush: () => {},
    loadAppInsights: () => {},
    addTelemetryInitializer: () => {},
    context: { telemetryTrace: { traceID: null, name: null } },
    initializeWhenReady: () => {}, // Will be overridden
  };
}

/**
 * Setup page visibility handling to reduce tracking in background tabs
 */
function setupPageVisibilityHandling(appInsights) {
  document.addEventListener("visibilitychange", () => {
    const wasVisible = isPageVisible;
    isPageVisible = !document.hidden;

    if (document.hidden && wasVisible) {
      // Page is now hidden - flush pending data and reduce tracking
      const now = Date.now();
      if (now - lastFlushTime > FLUSH_THROTTLE_MS) {
        appInsights.flush();
        lastFlushTime = now;
      }
    } else if (!document.hidden && !wasVisible) {
      // Page is now visible - resume normal tracking
      if (import.meta.env.VITE_NODE_ENV !== "production") {
        console.log("Application Insights: Resumed tracking for visible page");
      }
    }
  });
}

/**
 * Throttled flush function to prevent excessive flushing
 */
function throttledFlush(appInsights) {
  const now = Date.now();
  if (now - lastFlushTime > FLUSH_THROTTLE_MS && isPageVisible) {
    appInsights.flush();
    lastFlushTime = now;
  }
}

/**
 * Track route changes as page views with AppInsights
 * @param options
 */
function setupPageTracking(options, Vue) {
  const router = options.router;
  const baseName = options.baseName || "(Vue 3 App)";

  router.beforeEach((route, from, next) => {
    // Only track if App Insights is properly initialized
    if (appInsightsInitialized && isPageVisible) {
      const name = baseName + " / " + route.name;
      Vue.appInsights.context.telemetryTrace.traceID = generateW3CId();
      Vue.appInsights.context.telemetryTrace.name = route.name;
      Vue.appInsights.startTrackPage(name);
    }
    next();
  });

  router.afterEach((route) => {
    // Only track if App Insights is properly initialized
    if (appInsightsInitialized && isPageVisible) {
      const commonStore = useCommonStore();
      const loginStore = useLoginStore();

      const name = baseName + " / " + route.name;
      const url = location.protocol + "//" + location.host + route.fullPath;
      const _token = loginStore.authToken;
      const _userId = commonStore.fullUserInfo.userId;
      const _traceUser = commonStore.fullUserInfo.traceUser;

      Vue.appInsights.stopTrackPage(name, url, {
        AuthToken: _token,
        UserID: _userId,
        TraceUser: _traceUser,
      });

      // Use throttled flush instead of flushing on every route change
      throttledFlush(Vue.appInsights);
    }
  });
}

// auto install for navigator
if (typeof window !== "undefined" && window.Vue) {
  window.Vue.use(install);
}

export default install;
